import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useNotification } from '../context/NotificationContext';
import { getStatusLabel } from '../utils/productStatus';

const API_BASE_URL = 'https://api.iraq-parts.com';

const ProductCard = ({ product, allModels = [] }) => {
  const { addToCart } = useCart();
  const { success } = useNotification();
  
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect fill="%23ddd" width="300" height="300"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="18" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_BASE_URL}${imageUrl}`;
  };

  const handleAddToCart = () => {
    addToCart(product, 1);
    success(`تم إضافة ${product.name} إلى السلة`);
  };

  // Get compatible models
  const getCompatibleModels = () => {
    if (!product.car_models || !allModels.length) return [];
    
    try {
      const modelIds = typeof product.car_models === 'string' 
        ? JSON.parse(product.car_models) 
        : product.car_models;
      
      return allModels
        .filter(model => modelIds.includes(model.id) || modelIds.includes(String(model.id)))
        .map(model => model.name);
    } catch (error) {
      return [];
    }
  };

  const compatibleModels = getCompatibleModels();

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
      <Link to={`/product/${product.id}`} className="relative block">
        <img
          src={getImageUrl(product.image_url)}
          alt={product.name}
          className="w-full h-40 sm:h-48 object-cover"
          loading="lazy"
          decoding="async"
        />
        {/* Product Status Badge */}
        {product.product_status && (
          <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-lg">
            {getStatusLabel(product.product_status)}
          </span>
        )}
      </Link>
      
      <div className="p-3 sm:p-4 flex flex-col flex-grow">
        <Link to={`/product/${product.id}`} className="flex-grow">
          <h3 className="text-sm sm:text-lg font-semibold text-gray-800 hover:text-primary mb-2 line-clamp-2 min-h-[2.5rem] sm:min-h-[3rem]">
            {product.name}
          </h3>
        </Link>
        
        {product.part_number && (
          <p className="text-xs sm:text-sm text-gray-500 mb-1.5 sm:mb-2 truncate">
            رقم القطعة: {product.part_number}
          </p>
        )}
        
        {product.brand && (
          <p className="text-xs sm:text-sm text-gray-600 mb-1.5 sm:mb-2">
            العلامة: {product.brand}
          </p>
        )}
        
        {/* Compatible Models */}
        {compatibleModels.length > 0 && (
          <div className="mb-2 sm:mb-3 pb-2 sm:pb-3 border-b border-gray-200">
            <p className="text-xs text-gray-500 mb-1 text-right">متوافق مع:</p>
            <div className="flex flex-wrap gap-1">
              {compatibleModels.slice(0, 2).map((modelName, index) => (
                <span
                  key={index}
                  className="text-[10px] sm:text-xs bg-blue-50 text-primary px-1.5 sm:px-2 py-0.5 sm:py-1 rounded truncate max-w-[80px] sm:max-w-none"
                  title={modelName}
                >
                  {modelName}
                </span>
              ))}
              {compatibleModels.length > 2 && (
                <span className="text-[10px] sm:text-xs text-gray-500 px-1.5 sm:px-2 py-0.5 sm:py-1">
                  +{compatibleModels.length - 2}
                </span>
              )}
            </div>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row sm:flex-row-reverse sm:items-center sm:justify-between gap-2 sm:gap-0 mt-auto pt-2">
          <span className="text-lg sm:text-2xl font-bold text-primary text-center sm:text-right">
            {product.sell_price.toLocaleString()} د.ع
          </span>
          
          {product.stock_quantity > 0 ? (
            <button
              onClick={handleAddToCart}
              className="bg-primary text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm font-semibold w-full sm:w-auto"
            >
              أضف للسلة
            </button>
          ) : (
            <span className="text-red-500 font-semibold text-xs sm:text-base text-center">غير متوفر</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
